import { ProductImageFacing, ProductResponse } from 'lib/services/models/content-response';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { Base, Data } from '@generated/Foundation.GMI.model';
import { ProductHeroProps } from './ProductHero';
import { ProcessedBreadcrumbItem } from 'lib/page-props-factory/plugins/breadcrumb';

const maxProductHeroImages = 4;

/**
 * Extracts hero data from a ProductResponse object
 * @param product - The full product response object
 * @param siteSettings - Optional site settings for product hero settings
 * @returns ProductHeroProps object with only the properties needed for the product hero
 */
export function extractProductHeroData(
  product: ProductResponse,
  productRouteDetails?: Base.BasePage,
  breadcrumbData?: ProcessedBreadcrumbItem[] | null,
  siteSettings?:
    | (SiteSettings & {
        productDetailsSiteSettings?: Data.Settings.ProductDetailSettings;
      })
    | null
): ProductHeroProps {
  const { productDetailsSiteSettings, productDetailSettings } = siteSettings || {};
  const facingFields =
    (
      productRouteDetails?.fields as {
        facingFields?: { length: number; [key: number]: { name: string } };
      }
    )?.facingFields || [];

  const productHeroSettings = {
    hidePdpHero: productDetailsSiteSettings?.fields?.hideProductHero?.value,
    hidePdpHeroPearCommerceApp: (
      productRouteDetails?.fields as { hidePearCommerce?: { value?: boolean } }
    )?.hidePearCommerce?.value,
  };

  // Prepare images to display based on available assets and standard image.
  // Treat the Sitecore-configured facings as an ordered priority list rather
  // than the only valid images: iterate through the configured facings in
  // order, add each one that has a matching Content Service asset, skip any
  // configured facing that is missing from the response, avoid duplicates, and
  // stop once the maximum number of images has been collected.
  const maxImagesToDisplay =
    (productDetailSettings?.fields?.maxImagesOnHero as { value?: number })?.value ||
    maxProductHeroImages;
  const imagesToDisplay = [];

  if (facingFields.length === 0 || product?.Assets?.length === 0) {
    imagesToDisplay.push({
      imageUrl: product?.StandardImageUrl || product?.FallbackImageUrl || '',
      imageAlt: product?.FallbackImageAlt || product?.Title,
    });
  } else {
    const selectedImageUrls = new Set<string>();
    for (let i = 0; i < facingFields.length && imagesToDisplay.length < maxImagesToDisplay; i++) {
      const facingFieldName = facingFields[i]?.name as ProductImageFacing;
      if (!facingFieldName) {
        continue;
      }
      const assetUrl: string | undefined = product?.Assets?.find(
        (asset) => asset.ImageFacing === facingFieldName
      )?.Url;
      if (assetUrl && !selectedImageUrls.has(assetUrl)) {
        selectedImageUrls.add(assetUrl);
        imagesToDisplay.push({
          imageUrl: assetUrl,
          imageAlt: product.Title,
        });
      }
    }

    if (imagesToDisplay.length === 0) {
      imagesToDisplay.push({
        imageUrl: product?.StandardImageUrl || product?.FallbackImageUrl || '',
        imageAlt: product?.FallbackImageAlt || product?.Title,
      });
    }
  }

  return {
    imagesToDisplay: imagesToDisplay,
    contentId: product.ContentId,
    ratingAndReviewContentId: product.RatingAndReviewContentId,
    metadataIds: product.MetadataIds,
    title: product.Title,
    pageUrl: product.PageUrl,
    createdDate: product.CreatedDate ?? '',
    urlBreadcrumbs: breadcrumbData ?? [],
    pdpHeroSettings: {
      ...productHeroSettings,
    },
  };
}
